import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { isValidSearchDate } from 'utils/search/searchDateParam';
import OpenTicketSearchContainer from './OpenTicketSearchContainer';

/**
 * Wraps OpenTicketSearchContainer with the same date-validity guard as
 * ValidatedSearchProcessor. Covers both open-ticket routes in App.js: the dated round-trip
 * one (`/search/open-ticket/:originSlug/:destinationSlug/:departureDate/:returnDate/`, where
 * an invalid date used to reach `createSearch`/`transitionTo` as the literal "Invalid date")
 * and the dateless one (`/search/open-ticket/:originSlug/:destinationSlug`, where
 * `departureDate`/`returnDate` are simply absent and validation is skipped).
 * @param {object} routeProps - React Router route props (match, location, history).
 * @returns {JSX.Element} OpenTicketSearchContainer, or a redirect when dates are invalid.
 */
const ValidatedOpenTicketSearch = ({ match, location, ...routeProps }) => {
  const { originSlug, destinationSlug, departureDate, returnDate } = match.params;
  const datesValid =
    (!departureDate || isValidSearchDate(departureDate)) &&
    (!returnDate || isValidSearchDate(returnDate));

  if (!datesValid) {
    return (
      <Redirect
        to={{ pathname: `/search/${originSlug}/${destinationSlug}`, search: location.search }}
      />
    );
  }

  return <OpenTicketSearchContainer match={match} location={location} {...routeProps} />;
};

ValidatedOpenTicketSearch.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      originSlug: PropTypes.string,
      destinationSlug: PropTypes.string,
      departureDate: PropTypes.string,
      returnDate: PropTypes.string,
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
};

export default ValidatedOpenTicketSearch;
